import { useParams, Link } from "wouter";
import { trpc } from "../lib/trpc";
import Header from "../components/Header";
import Footer from "../components/Footer";

// カテゴリに応じたUnsplash画像キーワード
const CATEGORY_KEYWORDS: Record<string, string> = {
  "おすすめ": "family,cafe",
  "エリア特集": "tokyo,street",
  "季節のおでかけ": "park,children",
  "子育てコラム": "mother,child",
  "お役立ち情報": "baby,stroller",
};

function getDemoImage(articleId: number, category?: string): string {
  const keyword = category ? (CATEGORY_KEYWORDS[category] || "family,cafe") : "family,cafe";
  return `https://source.unsplash.com/featured/1200x600/?${keyword}&sig=${articleId}`;
}

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading, error } = trpc.articles.getBySlug.useQuery(
    { slug: slug ?? "" },
    { enabled: !!slug }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="container pt-24 pb-12">
          <div className="mx-auto max-w-3xl">
            <div className="h-8 w-2/3 animate-pulse rounded bg-stone-200" />
            <div className="mt-4 h-64 animate-pulse rounded-xl bg-stone-200" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="container pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-stone-900">
            記事が見つかりませんでした
          </h1>
          <Link href="/articles" className="mt-4 inline-block text-teal-600">
            コラム一覧に戻る
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const publishedDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      <article className="container pt-24 pb-12">
        <div className="mx-auto max-w-3xl">
          {/* Breadcrumb */}
          <nav className="mb-4 text-sm text-stone-500">
            <Link href="/" className="hover:text-teal-600">
              ホーム
            </Link>
            {" > "}
            <Link href="/articles" className="hover:text-teal-600">
              コラム
            </Link>
            {" > "}
            <span className="text-stone-900">{article.title}</span>
          </nav>

          {/* Category & Date */}
          <div className="mb-4 flex items-center gap-4 text-sm">
            <span className="rounded-full bg-teal-100 px-3 py-1 text-teal-700">
              {article.category}
            </span>
            {publishedDate && (
              <time className="text-stone-500">{publishedDate}</time>
            )}
            <span className="text-stone-400">
              {article.viewCount.toLocaleString()} views
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-stone-900 sm:text-4xl">
            {article.title}
          </h1>

          {/* Excerpt */}
          {article.excerpt && (
            <p className="mt-4 text-lg text-stone-600">{article.excerpt}</p>
          )}

          {/* Thumbnail */}
          <div className="mt-8 overflow-hidden rounded-xl bg-stone-100">
            <img
              src={article.thumbnailUrl || getDemoImage(article.id, article.category)}
              alt={article.title}
              className="w-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = `
                  <div class="flex h-64 w-full items-center justify-center bg-gradient-to-br from-amber-50 to-teal-50">
                    <span class="text-6xl">📝</span>
                  </div>
                `;
              }}
            />
          </div>

          {/* Content */}
          <div
            className="prose prose-stone mt-8 max-w-none prose-headings:text-stone-900 prose-a:text-teal-600"
            dangerouslySetInnerHTML={{ __html: formatContent(article.content) }}
          />

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-8 border-t border-stone-200 pt-6">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-stone-100 px-3 py-1 text-sm text-stone-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Back link */}
          <div className="mt-12 border-t border-stone-200 pt-6">
            <Link href="/articles" className="text-teal-600 hover:underline">
              ← コラム一覧に戻る
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

/**
 * Simple markdown-like formatting
 * In production, use a proper markdown parser like marked or remark
 */
function formatContent(content: string): string {
  return content
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/^(.+)$/gm, "<p>$1</p>")
    .replace(/<p><h/g, "<h")
    .replace(/<\/h(\d)><\/p>/g, "</h$1>");
}
